import { Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Calendar, Package, Truck, BookOpen, Users, List, Plus } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useGetMyBookings } from "~/services/booking";
import { TBooking, TServiceTicket, TProject, TClient } from "~/types";
import { PaginationResponse } from "~/services/type";
import { RESPONSIVE_CLASSES } from "~/utils/responsive";
import { ServiceDetailSidebar } from "./service-detail-sidebar";
import { BookingDetailSidebar } from "./booking-detail-sidebar";
import { BookingTable } from "./booking-table";
import { getClientBookingRelationship } from "~/utils/client-booking-filter";
import { useAuth } from "~/store";
import { JobStatus, ServiceType } from "~/utils/constants";
import { capitalize, calculateCubicFeetInventory } from "~/utils";
import dayjs from "dayjs";
import { ServicesCalendarView } from "../overview/services-calendar-view";
import { RequestServiceSidebar } from "../overview/request-service-sidebar";
import { useGetSubClients } from "~/services/client/client.queries";
import { ClientTutorialTip } from "../tutorial/tutorial-tip";

const { Title, Text } = Typography;

export const Bookings: React.FC = () => {
  const [selectedService, setSelectedService] = useState<TServiceTicket | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<TBooking | null>(null);
  const [activeView, setActiveView] = useState<"bookings" | "services" | "affiliated" | "projects" | "subclients">("bookings");
  const [serviceStatusFilter, setServiceStatusFilter] = useState<"all" | "estimated" | "scheduled" | "in-progress" | "completed">("all");
  const [servicesViewType, setServicesViewType] = useState<"list" | "calendar">("list");
  const [sortBy, setSortBy] = useState<"date" | "status" | "type" | "project">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isRequestServiceSidebarOpen, setIsRequestServiceSidebarOpen] = useState(false);
  const { user } = useAuth();
  const currentClientId = user?.client?.id;

  // Use the client-specific API to get all bookings for the logged-in client
  const { data: myBookings, isLoading } = useGetMyBookings({
    page: 1,
    pageSize: 100, // Max allowed by backend validation
  });

  // Fetch subclients
  const { data: subClientsData, isLoading: subClientsLoading } = useGetSubClients(
    currentClientId || "",
    {
      enabled: !!currentClientId && !user?.client?.isSubClient,
    }
  );

  const subClients = subClientsData?.items || [];
  const hasSubClients = subClients.length > 0;

  const handleServiceSelect = (service: TServiceTicket | null, booking: TBooking | null) => {
    setSelectedService(service);
    setSelectedBooking(booking);
  };

  const handleCloseSidebar = () => {
    setSelectedService(null);
    setSelectedBooking(null);
  };

  const isSidebarOpen = selectedService !== null;
  const isRequestServiceSidebarOpenState = isRequestServiceSidebarOpen && !isSidebarOpen;

  // Filter bookings where user is the main booking client
  const mainBookings = useMemo(() => {
    if (!myBookings?.items || !currentClientId) return [];
    return myBookings.items.filter(booking => booking.clientId === currentClientId);
  }, [myBookings, currentClientId]);

  // Get all service tickets (excluding affiliated)
  const allServiceTickets = useMemo(() => {
    if (!myBookings?.items || !currentClientId) return [];
    const services: Array<{ service: TServiceTicket; booking: TBooking }> = [];
    
    myBookings.items.forEach(booking => {
      const relationship = getClientBookingRelationship(booking, currentClientId);
      if (relationship.relationship === 'main-client') {
        booking.serviceTicket?.forEach(service => {
          services.push({ service, booking });
        });
      }
    });
    
    return services;
  }, [myBookings, currentClientId]);

  // Filter and sort service tickets
  const filteredAndSortedServiceTickets = useMemo(() => {
    let filtered = allServiceTickets;
    
    // Filter by status
    if (serviceStatusFilter !== "all") {
      filtered = allServiceTickets.filter(({ service }) => {
        switch (serviceStatusFilter) {
          case "estimated":
            return service.status === JobStatus.ESTIMATED;
          case "scheduled":
            return service.status === JobStatus.SCHEDULED;
          case "in-progress":
            return service.status === JobStatus.IN_PROGRESS;
          case "completed":
            return service.status === JobStatus.COMPLETED;
          default:
            return true;
        }
      });
    }
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case "date":
          const dateA = a.service.serviceDatetime ? new Date(a.service.serviceDatetime).getTime() : 0;
          const dateB = b.service.serviceDatetime ? new Date(b.service.serviceDatetime).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case "status":
          comparison = a.service.status.localeCompare(b.service.status);
          break;
        case "type":
          comparison = a.service.serviceType.localeCompare(b.service.serviceType);
          break;
        case "project":
          const projectA = a.booking.project?.name || a.booking.sidemark || "";
          const projectB = b.booking.project?.name || b.booking.sidemark || "";
          comparison = projectA.localeCompare(projectB);
          break;
      }
      
      return sortOrder === "asc" ? comparison : -comparison;
    });
    
    return sorted;
  }, [allServiceTickets, serviceStatusFilter, sortBy, sortOrder]);

  // Group bookings by project
  const projectsData = useMemo(() => {
    if (!myBookings?.items || !currentClientId) return [];
    
    const projectMap = new Map<string, {
      project: TProject;
      bookings: TBooking[];
      services: TServiceTicket[];
    }>();

    myBookings.items.forEach(booking => {
      const relationship = getClientBookingRelationship(booking, currentClientId);
      if (relationship.relationship === 'main-client' && booking.project) {
        const projectId = booking.project.id;
        if (!projectMap.has(projectId)) {
          projectMap.set(projectId, {
            project: booking.project,
            bookings: [],
            services: [],
          });
        }
        const projectData = projectMap.get(projectId)!;
        projectData.bookings.push(booking);
        booking.serviceTicket?.forEach(service => {
          projectData.services.push(service);
        });
      }
    });

    return Array.from(projectMap.values());
  }, [myBookings, currentClientId]);

  // Get affiliated services
  const affiliatedServices = useMemo(() => {
    if (!myBookings?.items || !currentClientId) return [];
    const services: Array<{ service: TServiceTicket; booking: TBooking }> = [];
    
    myBookings.items.forEach(booking => {
      const relationship = getClientBookingRelationship(booking, currentClientId);
      if (relationship.relationship === 'service-client') {
        relationship.visibleServiceTickets.forEach(service => {
          services.push({ service, booking });
        });
      }
    });
    
    return services;
  }, [myBookings, currentClientId]);

  // Service ticket counts
  const serviceCounts = useMemo(() => {
    const estimated = allServiceTickets.filter(s => s.service.status === JobStatus.ESTIMATED).length;
    const scheduled = allServiceTickets.filter(s => s.service.status === JobStatus.SCHEDULED).length;
    const inProgress = allServiceTickets.filter(s => s.service.status === JobStatus.IN_PROGRESS).length;
    const completed = allServiceTickets.filter(s => s.service.status === JobStatus.COMPLETED).length;
    return { estimated, scheduled, inProgress, completed, total: allServiceTickets.length };
  }, [allServiceTickets]);

  const getServiceTypeIcon = (type: ServiceType) => {
    switch (type) {
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.COLLECTION:
        return <Package size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusBadge = (status: JobStatus) => {
    // In Progress is special - just italic black text, no box
    if (status === JobStatus.IN_PROGRESS) {
      return (
        <span className="text-[#1a1a2e] italic font-bold text-xs uppercase">
          In Progress
        </span>
      );
    }
    
    // Scheduled is special - just black text, no box, no italics
    if (status === JobStatus.SCHEDULED) {
      return (
        <span className="text-[#1a1a2e] font-bold text-xs uppercase">
          Scheduled
        </span>
      );
    }
    
    const statusConfig = {
      [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
      [JobStatus.COMPLETED]: { text: "Completed", className: "bg-[#1a1a2e] text-white", extraClass: "" },
      [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
      [JobStatus.ESTIMATED]: { text: "Estimated", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    };
    const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" };
    return (
      <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg ${config.className}`}>
        {config.text}
      </span>
    );
  };

  // Helper function to get client name
  const getClientName = (client: TServiceTicket['client']) => {
    if (!client) return "-";
    if (client.companyName) return client.companyName;
    const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();
    return fullName || client.clientRef || "-";
  };

  const serviceColumns: ColumnsType<{ service: TServiceTicket; booking: TBooking }> = [
    {
      title: "Date",
      key: "serviceDatetime",
      width: 150,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking }) => {
        const date = record.service.serviceDatetime;
        if (!date) return " - ";
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dayjs(dateObj).format('ddd, MMM D, YYYY');
      },
    },
    {
      title: "Service Ref",
      key: "serial",
      width: 120,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking }) => (
        <Text strong>{record.service.serial || " - "}</Text>
      ),
    },
    {
      title: "Project / Sidemark",
      key: "project",
      width: 250,
      ellipsis: true,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking }) => {
        const projectName = record.booking.project?.name;
        const sidemark = record.booking.sidemark;
        const serviceClient = record.service.client;
        const bookingClient = record.booking.client;

        // Build project/sidemark display
        let projectDisplay = "";
        if (projectName && sidemark) {
          projectDisplay = `${projectName} / ${sidemark}`;
        } else if (projectName) {
          projectDisplay = projectName;
        } else if (sidemark) {
          projectDisplay = sidemark;
        } else {
          projectDisplay = "-";
        }

        // Determine if this is a subclient service:
        // 1. Service client is a subclient (isSubClient flag), OR
        // 2. Service client's parent is the current user, OR
        // 3. Booking client's parent is the current user (booking belongs to subclient)
        const isServiceClientSubclient = serviceClient?.isSubClient || serviceClient?.parentClientId === currentClientId;
        const isBookingClientSubclient = bookingClient?.parentClientId === currentClientId;

        // Get the subclient name - prefer booking client if it's the subclient, otherwise service client
        let subClientName = "";
        if (isBookingClientSubclient && bookingClient) {
          subClientName = getClientName(bookingClient);
        } else if (isServiceClientSubclient && serviceClient) {
          subClientName = getClientName(serviceClient);
        }

        if (subClientName) {
          return (
            <div className="flex flex-col">
              <span>{projectDisplay}</span>
              <span className="font-bold text-gray-600">--{subClientName}--</span>
            </div>
          );
        }

        return projectDisplay;
      },
    },
    {
      title: "Type",
      key: "serviceType",
      width: 120,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking }) => {
        const text = record.service.serviceType;
        return (
          <div className="flex items-center gap-1">
            {getServiceTypeIcon(text)}
            <span>{text ? capitalize(text) : " - "}</span>
          </div>
        );
      },
    },
    {
      title: "Booking Ref",
      key: "bookingRef",
      width: 140,
      ellipsis: true,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking }) => (
        <Text>{record.booking.referenceNumber || " - "}</Text>
      ),
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      ellipsis: true,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking }) => {
        const location = record.service.location;
        if (!location) return "-";
        return `${location.city || ""}${location.city && location.state ? ", " : ""}${location.state || ""}`.trim() || "-";
      },
    },
    {
      title: "Container Info",
      key: "containerInfo",
      width: 150,
      align: "center",
      render: (_: any, record: { service: TServiceTicket; booking: TBooking }) => {
        // Get all service tickets for this booking
        const allServiceTickets = record.booking.serviceTicket || [];
        
        // Get unique inventories across all service tickets in the booking (avoid duplicates)
        const inventoryMap = new Map<string, any>();
        allServiceTickets.forEach((ticket) => {
          ticket.inventories?.forEach((inventory) => {
            if (!inventoryMap.has(inventory.id)) {
              inventoryMap.set(inventory.id, inventory);
            }
          });
        });
        const uniqueInventories = Array.from(inventoryMap.values());
        const uniqueCount = uniqueInventories.length;
        
        // Calculate volume from unique inventories
        const stats = calculateCubicFeetInventory(uniqueInventories);
        const totalVolume = typeof stats.totalCubicFeet === 'number' 
          ? stats.totalCubicFeet 
          : parseFloat(stats.totalCubicFeet.toString()) || 0;

        return (
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1">
              <Package size={14} className="text-[#1a1a2e]" />
              <Text strong className="text-[#1a1a2e]">{uniqueCount}</Text>
            </div>
            <Text className="text-xs text-[#1a1a2e]">
              {totalVolume.toFixed(1)} ft³
            </Text>
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking }) => 
        getStatusBadge(record.service.status),
    },
  ];

  const handleRowClick = (record: { service: TServiceTicket; booking: TBooking }) => {
    if (selectedService?.id === record.service.id) {
      handleServiceSelect(null, null);
    } else {
      handleServiceSelect(record.service, record.booking);
    }
  };

  const getRowClassName = (record: { service: TServiceTicket; booking: TBooking }) => {
    const baseClass = "cursor-pointer hover:bg-[#fafafa] border-b border-[#e5e5e5]";
    if (selectedService?.id === record.service.id) {
      return `${baseClass} bg-[#1a1a2e] text-white`;
    }
    return baseClass;
  };

  return (
    <div className="flex min-h-0 relative bg-white">
      {/* Main Content */}
      <div
        className={`flex-1 min-w-0 max-w-full overflow-x-hidden transition-all duration-300 ease-in-out ${
          isSidebarOpen ? "lg:mr-[600px]" : ""
        }`}
      >
        <div className={`${RESPONSIVE_CLASSES.container} bg-white min-h-0 w-full max-w-full overflow-x-hidden`}>
          <div className={RESPONSIVE_CLASSES.marginBottom}>
            <div className="border border-[#e5e5e5] rounded-lg inline-block px-4 py-2 mb-2">
              <Title level={2} className={`${RESPONSIVE_CLASSES.titleText} font-bold text-[#1a1a2e] !mb-0 uppercase tracking-wide`}>
              Services
            </Title>
            </div>
            <p className={`${RESPONSIVE_CLASSES.bodyText} text-[#1a1a2e] font-medium`}>
              Manage your bookings, service tickets, and projects
            </p>
          </div>

          {/* View Selector - Neo Brutalist Button Group */}
          <div className="mb-6 flex items-center gap-4 flex-wrap">
            <div className="border border-[#e5e5e5] rounded-lg flex-1">
              <div className={`grid gap-0 ${hasSubClients ? "grid-cols-2 lg:grid-cols-5" : "grid-cols-2 lg:grid-cols-4"}`}>
                <ClientTutorialTip text="View all your bookings — each booking groups related service tickets together." placement="bottom">
                  <button
                    onClick={() => setActiveView("bookings")}
                    className={`px-4 py-3 text-left font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                      activeView === "bookings"
                        ? "bg-[#1a1a2e] text-white"
                        : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>Bookings</span>
                      <span className="ml-auto font-mono">{mainBookings.length}</span>
                    </div>
                  </button>
                </ClientTutorialTip>
                <ClientTutorialTip text="Flat list of all individual service tickets — collections, deliveries, installations, etc. Filter by status." placement="bottom">
                  <button
                    onClick={() => setActiveView("services")}
                    className={`px-4 py-3 text-left font-bold text-sm uppercase tracking-wide border-r-2 lg:border-r border-[#e5e5e5] last:border-r-0 transition-all ${
                      activeView === "services"
                        ? "bg-[#1a1a2e] text-white"
                        : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Package size={16} />
                      <span>Services</span>
                      <span className="ml-auto font-mono">{serviceCounts.total}</span>
                    </div>
                  </button>
                </ClientTutorialTip>
                {affiliatedServices.length > 0 && (
                  <ClientTutorialTip text="Services where your items appear on another client's booking — e.g. shared shipments or gallery loans." placement="bottom">
                    <button
                      onClick={() => setActiveView("affiliated")}
                      className={`px-4 py-3 text-left font-bold text-sm uppercase tracking-wide border-r-2 lg:border-r border-[#e5e5e5] last:border-r-0 transition-all ${
                        activeView === "affiliated"
                          ? "bg-[#1a1a2e] text-white"
                          : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Users size={16} />
                        <span>Affiliated</span>
                        <span className="ml-auto font-mono">{affiliatedServices.length}</span>
                      </div>
                    </button>
                  </ClientTutorialTip>
                )}
                <ClientTutorialTip text="Group your bookings and services by project for a higher-level view." placement="bottom">
                  <button
                    onClick={() => setActiveView("projects")}
                    className={`px-4 py-3 text-left font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                      activeView === "projects"
                        ? "bg-[#1a1a2e] text-white"
                        : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} />
                      <span>Projects</span>
                      <span className="ml-auto font-mono">{projectsData.length}</span>
                    </div>
                  </button>
                </ClientTutorialTip>
                {hasSubClients && (
                  <ClientTutorialTip text="View services and inventory for your sub-clients (e.g. artists, collectors you manage on behalf of)." placement="bottom">
                    <button
                      onClick={() => setActiveView("subclients")}
                      className={`px-4 py-3 text-left font-bold text-sm uppercase tracking-wide transition-all ${
                        activeView === "subclients"
                          ? "bg-[#1a1a2e] text-white"
                          : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Users size={16} />
                        <span>Subclients</span>
                        <span className="ml-auto font-mono">{subClients.length}</span>
                      </div>
                    </button>
                  </ClientTutorialTip>
                )}
              </div>
            </div>

            {/* Request Service Button */}
            <ClientTutorialTip text="Submit a new service request — collection, delivery, installation, or other art handling services.">
              <button
                onClick={() => setIsRequestServiceSidebarOpen(true)}
                className="border border-[#e5e5e5] rounded-lg bg-[#1a1a2e] text-white px-6 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all flex items-center gap-2 flex-shrink-0"
              >
                <Plus size={18} />
                Request Service
              </button>
            </ClientTutorialTip>
          </div>

          {/* Content Area */}
          <div className="border border-[#e5e5e5] rounded-lg bg-white">
            <div className="p-4">
              {activeView === "bookings" && (
                <BookingTable
                  bookings={mainBookings}
                  loading={isLoading}
                />
              )}

              {activeView === "services" && (
                  <div>
                    {/* View Toggle and Sorting Controls */}
                    <div className="mb-6 flex flex-wrap items-center gap-4">
                      {/* Calendar/List Toggle */}
                      <div className="border border-[#e5e5e5] rounded-lg inline-flex">
                        <button
                          onClick={() => setServicesViewType("list")}
                          className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                            servicesViewType === "list"
                              ? "bg-[#1a1a2e] text-white"
                              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                          }`}
                        >
                          <List size={18} className="inline mr-2" />
                          List
                        </button>
                        <button
                          onClick={() => setServicesViewType("calendar")}
                          className={`px-6 py-3 font-bold text-sm uppercase tracking-wide transition-all ${
                            servicesViewType === "calendar"
                              ? "bg-[#1a1a2e] text-white"
                              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                          }`}
                        >
                          <Calendar size={18} className="inline mr-2" />
                          Calendar
                        </button>
                      </div>

                      {/* Sorting Controls - Only show in list view */}
                      {servicesViewType === "list" && (
                        <div className="flex items-center gap-3">
                          <span className="text-base font-bold uppercase tracking-wide">Sort:</span>
                          <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as "date" | "status" | "type" | "project")}
                            className="border border-[#e5e5e5] rounded-lg px-4 py-2.5 font-bold text-sm uppercase bg-white"
                          >
                            <option value="date">Date</option>
                            <option value="status">Status</option>
                            <option value="type">Type</option>
                            <option value="project">Project</option>
                          </select>
                          <button
                            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                            className="border border-[#e5e5e5] rounded-lg px-4 py-2.5 font-bold text-sm uppercase bg-white hover:bg-[#fafafa]"
                          >
                            {sortOrder === "asc" ? "↑" : "↓"}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Status Filter Buttons - Neo Brutalist */}
                    <div className="mb-6 border border-[#e5e5e5] rounded-lg flex flex-wrap gap-2">
                        <button
                          onClick={() => setServiceStatusFilter("all")}
                          className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                            serviceStatusFilter === "all"
                              ? "bg-[#1a1a2e] text-white"
                              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                          }`}
                        >
                          All <span className="font-mono ml-1">({serviceCounts.total})</span>
                        </button>
                        <button
                          onClick={() => setServiceStatusFilter("estimated")}
                          className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                            serviceStatusFilter === "estimated"
                              ? "bg-[#1a1a2e] text-white"
                              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                          }`}
                        >
                          Estimates <span className="font-mono ml-1">({serviceCounts.estimated})</span>
                        </button>
                        <button
                          onClick={() => setServiceStatusFilter("scheduled")}
                          className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                            serviceStatusFilter === "scheduled"
                              ? "bg-[#1a1a2e] text-white"
                              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                          }`}
                        >
                          Scheduled <span className="font-mono ml-1">({serviceCounts.scheduled})</span>
                        </button>
                        <button
                          onClick={() => setServiceStatusFilter("in-progress")}
                          className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                            serviceStatusFilter === "in-progress"
                              ? "bg-[#1a1a2e] text-white"
                              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                          }`}
                        >
                          In Progress <span className="font-mono ml-1">({serviceCounts.inProgress})</span>
                        </button>
                        <button
                          onClick={() => setServiceStatusFilter("completed")}
                          className={`px-6 py-3 font-bold text-sm uppercase tracking-wide transition-all ${
                            serviceStatusFilter === "completed"
                              ? "bg-[#1a1a2e] text-white"
                              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
                          }`}
                        >
                          Completed <span className="font-mono ml-1">({serviceCounts.completed})</span>
                        </button>
                      </div>

                      {servicesViewType === "list" ? (
                        <div className="w-full max-w-full overflow-x-auto min-w-0">
                          <Table
                            columns={serviceColumns}
                            dataSource={filteredAndSortedServiceTickets}
                            loading={isLoading}
                            rowKey={(record) => record.service.id}
                            onRow={(record) => ({
                              onClick: () => handleRowClick(record),
                              className: getRowClassName(record),
                            })}
                            pagination={filteredAndSortedServiceTickets.length > 10 ? { pageSize: 10 } : false}
                            scroll={{ x: 800 }}
                            className="neo-brutalist-table"
                            locale={{
                              emptyText: `No ${serviceStatusFilter === "all" ? "" : serviceStatusFilter.replace("-", " ")} services found`,
                            }}
                          />
                        </div>
                      ) : (
                        <div className="border border-[#e5e5e5] rounded-lg p-4">
                          <ServicesCalendarView
                            services={filteredAndSortedServiceTickets.map(({ service, booking }) => {
                              const projectName = booking.project?.name;
                              const sidemark = booking.sidemark;
                              let projectDisplay = "";
                              if (projectName && sidemark) {
                                projectDisplay = `${projectName} / ${sidemark}`;
                              } else if (projectName) {
                                projectDisplay = projectName;
                              } else if (sidemark) {
                                projectDisplay = sidemark;
                              } else {
                                projectDisplay = "-";
                              }
                              
                              // Add subclient name if it's a subclient
                              if (service.client?.isSubClient) {
                                const subClientName = getClientName(service.client);
                                projectDisplay = projectDisplay !== "-" 
                                  ? `${projectDisplay} --${subClientName}--`
                                  : `--${subClientName}--`;
                              }
                              
                              return {
                                id: service.id,
                                serviceDatetime: service.serviceDatetime,
                                serial: service.serial,
                                project: projectDisplay,
                                serviceType: service.serviceType,
                                bookingRef: booking.referenceNumber,
                                location: service.location
                                  ? `${service.location.city || ""}${service.location.city && service.location.state ? ", " : ""}${service.location.state || ""}`.trim() || "-"
                                  : "-",
                                status: service.status,
                                rawBooking: booking,
                                rawService: service,
                              };
                            })}
                            onServiceClick={(service) => {
                              const found = allServiceTickets.find(s => s.service.id === service.id);
                              if (found) {
                                handleServiceSelect(found.service, found.booking);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}

              {activeView === "affiliated" && (
                <AffiliatedServicesView
                  affiliatedServices={affiliatedServices}
                  loading={isLoading}
                  onServiceSelect={handleServiceSelect}
                  selectedServiceId={selectedService?.id}
                />
              )}

              {activeView === "projects" && (
                <ProjectsView
                  projectsData={projectsData}
                  loading={isLoading}
                  onServiceSelect={handleServiceSelect}
                  selectedServiceId={selectedService?.id}
                />
              )}

              {activeView === "subclients" && (
                <SubclientsView
                  subClients={subClients}
                  myBookings={myBookings}
                  loading={isLoading || subClientsLoading}
                  onServiceSelect={handleServiceSelect}
                  selectedServiceId={selectedService?.id}
                  currentClientId={currentClientId || ""}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Service Detail Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] transition-all duration-300 ease-in-out z-50 max-w-[100vw] overflow-visible ${
          isSidebarOpen ? "w-[600px] translate-x-0" : "w-[600px] translate-x-full"
        }`}
      >
        <ServiceDetailSidebar
          service={selectedService}
          booking={selectedBooking}
          onClose={handleCloseSidebar}
        />
      </div>

      {/* Request Service Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] transition-all duration-300 ease-in-out z-50 max-w-[100vw] overflow-visible ${
          isRequestServiceSidebarOpenState ? "w-[420px] translate-x-0" : "w-[420px] translate-x-full"
        }`}
      >
        <RequestServiceSidebar
          open={isRequestServiceSidebarOpenState}
          onClose={() => setIsRequestServiceSidebarOpen(false)}
        />
      </div>

      {/* Overlay for mobile */}
      {(isSidebarOpen || isRequestServiceSidebarOpenState) && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
          onClick={() => {
            if (isSidebarOpen) handleCloseSidebar();
            if (isRequestServiceSidebarOpenState) setIsRequestServiceSidebarOpen(false);
          }}
        />
      )}
    </div>
  );
};

// Projects View Component
interface ProjectsViewProps {
  projectsData: Array<{
    project: TProject;
    bookings: TBooking[];
    services: TServiceTicket[];
  }>;
  loading?: boolean;
  onServiceSelect: (service: TServiceTicket | null, booking: TBooking | null) => void;
  selectedServiceId?: string | null;
}

const ProjectsView: React.FC<ProjectsViewProps> = ({
  projectsData,
  loading,
  onServiceSelect,
  selectedServiceId,
}) => {
  const getServiceTypeIcon = (type: ServiceType) => {
    switch (type) {
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.COLLECTION:
        return <Package size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusBadge = (status: JobStatus) => {
    // Scheduled is special - just black text, no box, no italics
    if (status === JobStatus.SCHEDULED) {
      return (
        <span className="text-[#1a1a2e] font-bold text-xs uppercase">
          Scheduled
        </span>
      );
    }
    
    const statusConfig: Record<JobStatus, { text: string; className: string; extraClass?: string }> = {
      [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
      [JobStatus.ESTIMATED]: { text: "Estimated", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
      [JobStatus.IN_PROGRESS]: { text: "In Progress", className: "bg-[#1a1a2e] text-white", extraClass: "italic border-4" },
      [JobStatus.SCHEDULED]: { text: "Scheduled", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
      [JobStatus.COMPLETED]: { text: "Completed", className: "bg-[#1a1a2e] text-white", extraClass: "" },
      [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    };
    const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" };
    return (
      <span className={`inline-block px-2 py-1 text-xs font-bold uppercase ${config.extraClass || "border-2"} border-[#e5e5e5] ${config.className}`}>
        {config.text}
      </span>
    );
  };

  const serviceColumns: ColumnsType<TServiceTicket & { bookingRef: string }> = [
    {
      title: "Date",
      dataIndex: "serviceDatetime",
      key: "serviceDatetime",
      render: (date: Date | string) => {
        if (!date) return " - ";
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dayjs(dateObj).format('MMM D, YYYY');
      },
    },
    {
      title: "Service Ref",
      dataIndex: "serial",
      key: "serial",
      render: (text: string) => <Text strong>{text || " - "}</Text>,
    },
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (text: ServiceType) => (
        <div className="flex items-center gap-1">
          {getServiceTypeIcon(text)}
          <span>{text ? capitalize(text) : " - "}</span>
        </div>
      ),
    },
    {
      title: "Booking Ref",
      dataIndex: "bookingRef",
      key: "bookingRef",
      render: (text: string) => <Text>{text || " - "}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: JobStatus) => getStatusBadge(status),
    },
  ];

  if (loading) {
    return <div className="text-center p-8">Loading projects...</div>;
  }

  if (projectsData.length === 0) {
    return <div className="text-center p-8 text-[#1a1a2e]">No projects found</div>;
  }

  return (
    <div className="space-y-3">
      {projectsData.map((projectData) => (
        <div key={projectData.project.id} className="border border-[#e5e5e5] rounded-lg bg-white">
          <div className="p-4 border-b border-[#e5e5e5]">
            <div className="flex items-center justify-between w-full">
              <div>
                <Text strong className="text-[#1a1a2e] font-bold">{projectData.project.name}</Text>
                {projectData.project.description && (
                  <Text className="ml-2 text-[#1a1a2e]">
                    {projectData.project.description}
                  </Text>
                )}
              </div>
              <div className="flex gap-4">
                <Text className="text-[#1a1a2e] font-bold">
                  {projectData.bookings.length} {projectData.bookings.length === 1 ? 'booking' : 'bookings'}
                </Text>
                <Text className="text-[#1a1a2e] font-bold">
                  {projectData.services.length} {projectData.services.length === 1 ? 'service' : 'services'}
                </Text>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <Text strong className="text-sm text-[#1a1a2e] font-bold uppercase tracking-wide">Bookings:</Text>
              <div className="mt-2 space-y-2">
                {projectData.bookings.map(booking => (
                  <div key={booking.id} className="p-2 border border-[#e5e5e5] rounded-lg bg-white">
                    <Text strong className="text-[#1a1a2e] font-bold">{booking.referenceNumber}</Text>
                    <Text className="ml-2 text-sm text-[#1a1a2e]">
                      {booking.sidemark ? `Sidemark: ${booking.sidemark}` : ''}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Text strong className="text-sm text-[#1a1a2e] font-bold uppercase tracking-wide">Services:</Text>
              <Table
                columns={serviceColumns}
                dataSource={projectData.services.map(service => ({
                  ...service,
                  key: service.id,
                  bookingRef: projectData.bookings.find(b => 
                    b.serviceTicket?.some(st => st.id === service.id)
                  )?.referenceNumber || '-',
                }))}
                pagination={false}
                size="small"
                className="neo-brutalist-table"
                onRow={(record) => ({
                  onClick: () => {
                    const booking = projectData.bookings.find(b => 
                      b.serviceTicket?.some(st => st.id === record.id)
                    );
                    onServiceSelect(record, booking || null);
                  },
                  className: selectedServiceId === record.id ? "bg-[#1a1a2e] text-white cursor-pointer border-b border-[#e5e5e5]" : "cursor-pointer hover:bg-[#fafafa] border-b border-[#e5e5e5]",
                })}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Affiliated Services View Component
interface AffiliatedServicesViewProps {
  affiliatedServices: Array<{
    service: TServiceTicket;
    booking: TBooking;
  }>;
  loading?: boolean;
  onServiceSelect: (service: TServiceTicket | null, booking: TBooking | null) => void;
  selectedServiceId?: string | null;
}

const AffiliatedServicesView: React.FC<AffiliatedServicesViewProps> = ({
  affiliatedServices,
  loading,
  onServiceSelect,
  selectedServiceId,
}) => {
  const getServiceTypeIcon = (type: ServiceType) => {
    switch (type) {
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.COLLECTION:
        return <Package size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusBadge = (status: JobStatus) => {
    // Scheduled is special - just black text, no box, no italics
    if (status === JobStatus.SCHEDULED) {
      return (
        <span className="text-[#1a1a2e] font-bold text-xs uppercase">
          Scheduled
        </span>
      );
    }
    
    const statusConfig = {
      [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
      [JobStatus.IN_PROGRESS]: { text: "In Progress", className: "bg-[#1a1a2e] text-white", extraClass: "italic border-4" },
      [JobStatus.COMPLETED]: { text: "Completed", className: "bg-[#1a1a2e] text-white", extraClass: "" },
      [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    };
    const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" };
    return (
      <span className={`inline-block px-2 py-1 text-xs font-bold uppercase ${config.extraClass || "border-2"} border-[#e5e5e5] ${config.className}`}>
        {config.text}
      </span>
    );
  };

  type AffiliatedServiceRow = {
    service: TServiceTicket;
    booking: TBooking;
    key: string;
  };

  const columns: ColumnsType<AffiliatedServiceRow> = [
    {
      title: "Date",
      key: "serviceDatetime",
      render: (_: any, record: AffiliatedServiceRow) => {
        const date = record.service.serviceDatetime;
        if (!date) return " - ";
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dayjs(dateObj).format('ddd, MMM D, YYYY');
      },
    },
    {
      title: "Service Ref",
      key: "serial",
      render: (_: any, record: AffiliatedServiceRow) => (
        <Text strong>{record.service.serial || " - "}</Text>
      ),
    },
    {
      title: "Booking Client",
      key: "bookingClient",
      render: (_: any, record: AffiliatedServiceRow) => 
        record.booking.client?.companyName || " - ",
    },
    {
      title: "Project / Sidemark",
      key: "project",
      render: (_: any, record: AffiliatedServiceRow) => {
        const projectName = record.booking.project?.name;
        const sidemark = record.booking.sidemark;
        if (projectName && sidemark) return `${projectName} / ${sidemark}`;
        if (projectName) return projectName;
        if (sidemark) return sidemark;
        return "-";
      },
    },
    {
      title: "Type",
      key: "serviceType",
      render: (_: any, record: AffiliatedServiceRow) => {
        const text = record.service.serviceType;
        return (
          <div className="flex items-center gap-1">
            {getServiceTypeIcon(text)}
            <span>{text ? capitalize(text) : " - "}</span>
          </div>
        );
      },
    },
    {
      title: "Location",
      key: "location",
      render: (_: any, record: AffiliatedServiceRow) => {
        const location = record.service.location;
        if (!location) return "-";
        return `${location.city || ""}${location.city && location.state ? ", " : ""}${location.state || ""}`.trim() || "-";
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, record: AffiliatedServiceRow) => 
        getStatusBadge(record.service.status),
    },
  ];

  return (
    <>
      <div className="mb-4 border border-[#e5e5e5] rounded-lg bg-white p-4">
        <div className="font-bold text-sm uppercase tracking-wide mb-1 text-[#1a1a2e]">Affiliated Services</div>
        <div className="text-sm text-[#1a1a2e]">
          These service tickets were completed as part of another client's booking where you were listed as a service client.
        </div>
      </div>
      <Table<AffiliatedServiceRow>
        columns={columns}
        dataSource={affiliatedServices.map(item => ({ ...item, key: item.service.id }))}
        loading={loading}
        pagination={affiliatedServices.length > 10 ? { pageSize: 10 } : false}
        scroll={{ x: 800 }}
        className="neo-brutalist-table"
        onRow={(record) => ({
          onClick: () => onServiceSelect(record.service, record.booking),
          className: selectedServiceId === record.service.id ? "bg-[#1a1a2e] text-white cursor-pointer border-b border-[#e5e5e5]" : "cursor-pointer hover:bg-[#fafafa] border-b border-[#e5e5e5]",
        })}
        locale={{
          emptyText: "No affiliated services found",
        }}
      />
    </>
  );
};

// Subclients View Component
interface SubclientsViewProps {
  subClients: TClient[];
  myBookings: PaginationResponse<TBooking> | undefined;
  loading?: boolean;
  onServiceSelect: (service: TServiceTicket | null, booking: TBooking | null) => void;
  selectedServiceId?: string | null;
  currentClientId: string;
}

const SubclientsView: React.FC<SubclientsViewProps> = ({
  subClients,
  myBookings,
  loading,
  onServiceSelect,
  selectedServiceId,
  currentClientId,
}) => {
  const [activeSubclientTab, setActiveSubclientTab] = useState<"services" | "bookings">("services");
  const [sortBy, setSortBy] = useState<"date" | "status" | "type" | "subclient">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [serviceStatusFilter, setServiceStatusFilter] = useState<"all" | "estimated" | "scheduled" | "in-progress" | "completed">("all");
  const [selectedSubclientBooking, setSelectedSubclientBooking] = useState<TBooking | null>(null);

  // Get all subclient services - includes ALL services from bookings that belong to subclients
  const subclientServices = useMemo(() => {
    if (!myBookings?.items || !currentClientId) return [];
    const services: Array<{ service: TServiceTicket; booking: TBooking; subClient: TClient }> = [];

    const subClientIds = new Set(subClients.map(sc => sc.id));

    myBookings.items.forEach(booking => {
      // First check if this booking belongs to a subclient
      const isBookingFromSubclient =
        (booking.clientId && subClientIds.has(booking.clientId)) ||
        (booking.client?.parentClientId === currentClientId);

      // Determine the subclient for this booking
      let bookingSubClient: TClient | null = null;
      if (booking.clientId && subClientIds.has(booking.clientId)) {
        bookingSubClient = subClients.find(sc => sc.id === booking.clientId) || null;
      }
      if (!bookingSubClient && booking.client?.parentClientId === currentClientId) {
        bookingSubClient = booking.client;
      }

      // If booking is from a subclient, include ALL its services
      if (isBookingFromSubclient && bookingSubClient) {
        booking.serviceTicket?.forEach(service => {
          services.push({ service, booking, subClient: bookingSubClient! });
        });
      } else {
        // Otherwise, check individual services for subclient involvement
        booking.serviceTicket?.forEach(service => {
          const isServiceFromSubclient =
            (service.clientId && subClientIds.has(service.clientId)) ||
            (service.client?.parentClientId === currentClientId);

          if (isServiceFromSubclient) {
            let serviceSubClient: TClient | null = null;
            if (service.clientId && subClientIds.has(service.clientId)) {
              serviceSubClient = subClients.find(sc => sc.id === service.clientId) || null;
            }
            if (!serviceSubClient && service.client?.parentClientId === currentClientId) {
              serviceSubClient = service.client;
            }

            if (serviceSubClient) {
              services.push({ service, booking, subClient: serviceSubClient });
            }
          }
        });
      }
    });

    return services;
  }, [myBookings, currentClientId, subClients]);

  // Get subclient bookings
  const subclientBookings = useMemo(() => {
    if (!myBookings?.items || !currentClientId) return [];
    const bookings: Array<{ booking: TBooking; subClient: TClient }> = [];

    // Create a set of subclient IDs for quick lookup
    const subClientIds = new Set(subClients.map(sc => sc.id));

    myBookings.items.forEach(booking => {
      // Check if booking belongs to a subclient by:
      // 1. Checking if booking.clientId is in subclients list, OR
      // 2. Checking if booking.client?.parentClientId matches current client (booking's client is a subclient)
      const isInSubClientsList = booking.clientId && subClientIds.has(booking.clientId);
      const isParentOfBookingClient = booking.client?.parentClientId === currentClientId;
      const isSubClientBooking = isInSubClientsList || isParentOfBookingClient;

      if (isSubClientBooking) {
        // Find the subclient - prefer from subClients list, fallback to booking.client
        let subClient: TClient | null = null;

        if (booking.clientId && subClientIds.has(booking.clientId)) {
          subClient = subClients.find(sc => sc.id === booking.clientId) || null;
        }

        // If not found in subClients list but booking.client is a subclient of current user
        if (!subClient && booking.client?.parentClientId === currentClientId) {
          subClient = booking.client;
        }

        if (subClient) {
          bookings.push({ booking, subClient });
        }
      }
    });

    return bookings;
  }, [myBookings, currentClientId, subClients]);

  // Filter and sort services
  const filteredAndSortedServices = useMemo(() => {
    let filtered = subclientServices;
    
    // Filter by status
    if (serviceStatusFilter !== "all") {
      filtered = subclientServices.filter(({ service }) => {
        switch (serviceStatusFilter) {
          case "estimated":
            return service.status === JobStatus.ESTIMATED;
          case "scheduled":
            return service.status === JobStatus.SCHEDULED;
          case "in-progress":
            return service.status === JobStatus.IN_PROGRESS;
          case "completed":
            return service.status === JobStatus.COMPLETED;
          default:
            return true;
        }
      });
    }
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case "date":
          const dateA = a.service.serviceDatetime ? new Date(a.service.serviceDatetime).getTime() : 0;
          const dateB = b.service.serviceDatetime ? new Date(b.service.serviceDatetime).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case "status":
          comparison = a.service.status.localeCompare(b.service.status);
          break;
        case "type":
          comparison = a.service.serviceType.localeCompare(b.service.serviceType);
          break;
        case "subclient":
          const nameA = a.subClient.companyName || `${a.subClient.firstName || ""} ${a.subClient.lastName || ""}`.trim() || "";
          const nameB = b.subClient.companyName || `${b.subClient.firstName || ""} ${b.subClient.lastName || ""}`.trim() || "";
          comparison = nameA.localeCompare(nameB);
          break;
      }
      
      return sortOrder === "asc" ? comparison : -comparison;
    });
    
    return sorted;
  }, [subclientServices, serviceStatusFilter, sortBy, sortOrder]);

  // Service counts
  const serviceCounts = useMemo(() => {
    const estimated = subclientServices.filter(s => s.service.status === JobStatus.ESTIMATED).length;
    const scheduled = subclientServices.filter(s => s.service.status === JobStatus.SCHEDULED).length;
    const inProgress = subclientServices.filter(s => s.service.status === JobStatus.IN_PROGRESS).length;
    const completed = subclientServices.filter(s => s.service.status === JobStatus.COMPLETED).length;
    return { estimated, scheduled, inProgress, completed, total: subclientServices.length };
  }, [subclientServices]);

  const getServiceTypeIcon = (type: ServiceType) => {
    switch (type) {
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.COLLECTION:
        return <Package size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusBadge = (status: JobStatus) => {
    if (status === JobStatus.IN_PROGRESS) {
      return (
        <span className="text-[#1a1a2e] italic font-bold text-xs uppercase">
          In Progress
        </span>
      );
    }
    
    if (status === JobStatus.SCHEDULED) {
      return (
        <span className="text-[#1a1a2e] font-bold text-xs uppercase">
          Scheduled
        </span>
      );
    }
    
    const statusConfig = {
      [JobStatus.DRAFT]: { text: "Draft", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
      [JobStatus.COMPLETED]: { text: "Completed", className: "bg-[#1a1a2e] text-white", extraClass: "" },
      [JobStatus.CANCELLED]: { text: "Cancelled", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
      [JobStatus.ESTIMATED]: { text: "Estimated", className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" },
    };
    const config = statusConfig[status] || { text: status, className: "bg-gray-200 text-[#1a1a2e]", extraClass: "" };
    return (
      <span className={`inline-block px-2 py-1 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg ${config.className}`}>
        {config.text}
      </span>
    );
  };

  // Helper function to get client name
  const getClientName = (client: TClient) => {
    if (client.companyName) return client.companyName;
    const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();
    return fullName || client.clientRef || "Unknown";
  };

  const serviceColumns: ColumnsType<{ service: TServiceTicket; booking: TBooking; subClient: TClient }> = [
    {
      title: "Date",
      key: "serviceDatetime",
      width: 150,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking; subClient: TClient }) => {
        const date = record.service.serviceDatetime;
        if (!date) return " - ";
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dayjs(dateObj).format('ddd, MMM D, YYYY');
      },
    },
    {
      title: "Service Ref",
      key: "serial",
      width: 120,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking; subClient: TClient }) => (
        <Text strong>{record.service.serial || " - "}</Text>
      ),
    },
    {
      title: "Subclient",
      key: "subclient",
      width: 180,
      ellipsis: true,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking; subClient: TClient }) => (
        <span className="font-bold italic">--{getClientName(record.subClient)}--</span>
      ),
    },
    {
      title: "Project / Sidemark",
      key: "project",
      width: 250,
      ellipsis: true,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking; subClient: TClient }) => {
        const projectName = record.booking.project?.name;
        const sidemark = record.booking.sidemark;
        if (projectName && sidemark) return `${projectName} / ${sidemark}`;
        if (projectName) return projectName;
        if (sidemark) return sidemark;
        return "-";
      },
    },
    {
      title: "Type",
      key: "serviceType",
      width: 120,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking; subClient: TClient }) => {
        const text = record.service.serviceType;
        return (
          <div className="flex items-center gap-1">
            {getServiceTypeIcon(text)}
            <span>{text ? capitalize(text) : " - "}</span>
          </div>
        );
      },
    },
    {
      title: "Booking Ref",
      key: "bookingRef",
      width: 140,
      ellipsis: true,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking; subClient: TClient }) => (
        <Text>{record.booking.referenceNumber || " - "}</Text>
      ),
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      ellipsis: true,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking; subClient: TClient }) => {
        const location = record.service.location;
        if (!location) return "-";
        return `${location.city || ""}${location.city && location.state ? ", " : ""}${location.state || ""}`.trim() || "-";
      },
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_: any, record: { service: TServiceTicket; booking: TBooking; subClient: TClient }) => 
        getStatusBadge(record.service.status),
    },
  ];

  const bookingColumns: ColumnsType<{ booking: TBooking; subClient: TClient }> = [
    {
      title: "Booking Ref",
      key: "referenceNumber",
      width: 140,
      ellipsis: true,
      render: (_: any, record: { booking: TBooking; subClient: TClient }) => (
        <Text strong>{record.booking.referenceNumber || " - "}</Text>
      ),
    },
    {
      title: "Subclient",
      key: "subclient",
      width: 180,
      ellipsis: true,
      render: (_: any, record: { booking: TBooking; subClient: TClient }) => (
        <span className="font-bold italic">--{getClientName(record.subClient)}--</span>
      ),
    },
    {
      title: "Project / Sidemark",
      key: "project",
      width: 250,
      ellipsis: true,
      render: (_: any, record: { booking: TBooking; subClient: TClient }) => {
        const projectName = record.booking.project?.name;
        const sidemark = record.booking.sidemark;
        if (projectName && sidemark) return `${projectName} / ${sidemark}`;
        if (projectName) return projectName;
        if (sidemark) return sidemark;
        return "-";
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_: any, record: { booking: TBooking; subClient: TClient }) => {
        const status = record.booking.status;
        return status ? capitalize(status) : "-";
      },
    },
    {
      title: "Services",
      key: "services",
      width: 100,
      align: "center",
      render: (_: any, record: { booking: TBooking; subClient: TClient }) => (
        <span>{record.booking.serviceTicket?.length || 0}</span>
      ),
    },
  ];

  if (loading) {
    return <div className="text-center p-8 text-[#1a1a2e]">Loading subclients data...</div>;
  }

  if (subClients.length === 0) {
    return <div className="text-center p-8 text-[#1a1a2e]">No subclients found</div>;
  }

  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Tab Navigation */}
      <div className="flex border border-[#e5e5e5] rounded-lg">
        <button
          onClick={() => setActiveSubclientTab("services")}
          className={`flex-1 px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
            activeSubclientTab === "services"
              ? "bg-[#1a1a2e] text-white"
              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
          }`}
        >
          Services <span className="font-mono ml-1">({subclientServices.length})</span>
        </button>
        <button
          onClick={() => setActiveSubclientTab("bookings")}
          className={`flex-1 px-6 py-3 font-bold text-sm uppercase tracking-wide transition-all ${
            activeSubclientTab === "bookings"
              ? "bg-[#1a1a2e] text-white"
              : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
          }`}
        >
          Bookings <span className="font-mono ml-1">({subclientBookings.length})</span>
        </button>
      </div>

      {/* Services Tab Content */}
      {activeSubclientTab === "services" && (
        <div className="w-full max-w-full overflow-x-hidden">
          <div className="mb-4 flex items-center justify-between flex-wrap gap-4">
            <div className="border border-[#e5e5e5] rounded-lg inline-block px-3 py-1">
              <h3 className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] !mb-0">
                Subclient Services ({subclientServices.length})
              </h3>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-base font-bold uppercase tracking-wide">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "date" | "status" | "type" | "subclient")}
                className="border border-[#e5e5e5] rounded-lg px-4 py-2.5 font-bold text-sm uppercase bg-white"
              >
                <option value="date">Date</option>
                <option value="status">Status</option>
                <option value="type">Type</option>
                <option value="subclient">Subclient</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                className="border border-[#e5e5e5] rounded-lg px-4 py-2.5 font-bold text-sm uppercase bg-white hover:bg-[#fafafa]"
              >
                {sortOrder === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>

          {/* Status Filter Buttons */}
          <div className="mb-6 border border-[#e5e5e5] rounded-lg flex flex-wrap">
            <button
              onClick={() => setServiceStatusFilter("all")}
              className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                serviceStatusFilter === "all"
                  ? "bg-[#1a1a2e] text-white"
                  : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
              }`}
            >
              All <span className="font-mono ml-1">({serviceCounts.total})</span>
            </button>
            <button
              onClick={() => setServiceStatusFilter("estimated")}
              className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                serviceStatusFilter === "estimated"
                  ? "bg-[#1a1a2e] text-white"
                  : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
              }`}
            >
              Estimates <span className="font-mono ml-1">({serviceCounts.estimated})</span>
            </button>
            <button
              onClick={() => setServiceStatusFilter("scheduled")}
              className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                serviceStatusFilter === "scheduled"
                  ? "bg-[#1a1a2e] text-white"
                  : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
              }`}
            >
              Scheduled <span className="font-mono ml-1">({serviceCounts.scheduled})</span>
            </button>
            <button
              onClick={() => setServiceStatusFilter("in-progress")}
              className={`px-6 py-3 font-bold text-sm uppercase tracking-wide border-r border-[#e5e5e5] transition-all ${
                serviceStatusFilter === "in-progress"
                  ? "bg-[#1a1a2e] text-white"
                  : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
              }`}
            >
              In Progress <span className="font-mono ml-1">({serviceCounts.inProgress})</span>
            </button>
            <button
              onClick={() => setServiceStatusFilter("completed")}
              className={`px-6 py-3 font-bold text-sm uppercase tracking-wide transition-all ${
                serviceStatusFilter === "completed"
                  ? "bg-[#1a1a2e] text-white"
                  : "bg-white text-[#1a1a2e] hover:bg-[#fafafa]"
              }`}
            >
              Completed <span className="font-mono ml-1">({serviceCounts.completed})</span>
            </button>
          </div>

          <div className="w-full max-w-full overflow-x-auto min-w-0">
            <Table
              columns={serviceColumns}
              dataSource={filteredAndSortedServices}
              loading={loading}
              rowKey={(record) => record.service.id}
              onRow={(record) => ({
                onClick: () => onServiceSelect(record.service, record.booking),
                className: selectedServiceId === record.service.id
                  ? "bg-[#1a1a2e] text-white cursor-pointer border-b border-[#e5e5e5]"
                  : "cursor-pointer hover:bg-[#fafafa] border-b border-[#e5e5e5]",
              })}
              pagination={filteredAndSortedServices.length > 10 ? { pageSize: 10 } : false}
              scroll={{ x: 1200 }}
              className="neo-brutalist-table"
              locale={{
                emptyText: `No ${serviceStatusFilter === "all" ? "" : serviceStatusFilter.replace("-", " ")} subclient services found`,
              }}
            />
          </div>
        </div>
      )}

      {/* Bookings Tab Content */}
      {activeSubclientTab === "bookings" && (
        <div className="w-full max-w-full overflow-x-hidden">
          <div className="mb-4 border border-[#e5e5e5] rounded-lg inline-block px-3 py-1">
            <h3 className="font-bold text-sm uppercase tracking-wide text-[#1a1a2e] !mb-0">
              Subclient Bookings ({subclientBookings.length})
            </h3>
          </div>
          <div className="w-full max-w-full overflow-x-auto min-w-0">
            <Table
              columns={bookingColumns}
              dataSource={subclientBookings}
              loading={loading}
              rowKey={(record) => record.booking.id}
              pagination={subclientBookings.length > 10 ? { pageSize: 10 } : false}
              scroll={{ x: 1000 }}
              className="neo-brutalist-table"
              onRow={(record) => ({
                onClick: () => setSelectedSubclientBooking(record.booking),
                className: selectedSubclientBooking?.id === record.booking.id
                  ? "bg-[#1a1a2e] text-white cursor-pointer border-b border-[#e5e5e5]"
                  : "cursor-pointer hover:bg-[#fafafa] border-b border-[#e5e5e5]",
              })}
              locale={{
                emptyText: "No subclient bookings found",
              }}
            />
          </div>
        </div>
      )}

      {/* Booking Detail Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full bg-white border-l border-[#e5e5e5] transition-all duration-300 ease-in-out z-50 ${
          selectedSubclientBooking ? "w-[600px] translate-x-0" : "w-[600px] translate-x-full"
        }`}
        style={{ maxWidth: 'calc(100vw - 0px)' }}
      >
        <BookingDetailSidebar
          booking={selectedSubclientBooking}
          onClose={() => setSelectedSubclientBooking(null)}
        />
      </div>

      {/* Overlay for mobile */}
      {selectedSubclientBooking && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 lg:hidden"
          onClick={() => setSelectedSubclientBooking(null)}
        />
      )}
    </div>
  );
};
